import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import config from "../../../config/config.js";
import { useAuth } from "../../login/AuthContext";
import { toast } from "react-toastify";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import "../../packages/Packages.css";
import "../../customers/customers.css";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  AddCircleOutline,
  Apps,
  Close,
  Delete,
  Edit,
  Extension,
  People,
  Save,
} from "@mui/icons-material";

const emptyForm = () => ({
  paketAdi: "Gastro",
  maxKullaniciSayisi: 1,
  sira: "",
  selectedEkModulIds: [],
});

export default function GastoraPackages() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [paketler, setPaketler] = useState([]);
  const [ekModuller, setEkModuller] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [ekModulDialogOpen, setEkModulDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [newEkModul, setNewEkModul] = useState({
    kod: "",
    adi: "",
    aciklama: "",
    parentModule: "GASTRO",
  });

  const fetchPaketler = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${config.baseURL}/gastora-paketler/paket-detay-list`, { headers });
      setPaketler(res.data || []);
    } catch {
      toast.error("Gastro Paketleri yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchEkModuller = useCallback(async () => {
    try {
      const res = await axios.get(`${config.baseURL}/gastora-ek-moduller`, { headers });
      setEkModuller(res.data || []);
    } catch {
      toast.error("Ek modül listesi yüklenemedi");
    }
  }, [token]);

  useEffect(() => {
    fetchPaketler();
    fetchEkModuller();
  }, [fetchPaketler, fetchEkModuller]);

  const availableEkModuller = ekModuller;

  const getNextSira = () => {
    if (!paketler.length) return 1;
    const max = Math.max(0, ...paketler.map((p) => Number(p.Sira) || 0));
    return max + 1;
  };

  const resetDrawer = () => {
    setDrawerOpen(false);
    setEditId(null);
    setForm(emptyForm());
  };

  const openCreateDrawer = () => {
    setEditId(null);
    setForm({ ...emptyForm(), sira: String(getNextSira()) });
    setDrawerOpen(true);
  };

  const openEditDrawer = async (paketId) => {
    try {
      const res = await axios.get(`${config.baseURL}/gastora-paketler/paket-detay/${paketId}`, { headers });
      const p = res.data;
      setEditId(p.Id);
      setForm({
        paketAdi: p.Adi || "Gastro",
        maxKullaniciSayisi: p.MaxKullaniciSayisi ?? 1,
        sira: p.Sira ?? "",
        selectedEkModulIds: p.selectedEkModulIds || p.EkModuller?.map((e) => e.Id) || [],
      });
      setDrawerOpen(true);
    } catch {
      toast.error("Paket detayı alınamadı");
    }
  };

  const openEkModulDialog = () => {
    setNewEkModul({
      kod: "",
      adi: "",
      aciklama: "",
      parentModule: "GASTRO",
    });
    setEkModulDialogOpen(true);
  };

  const toggleEkModul = (id) => {
    setForm((prev) => {
      const set = new Set(prev.selectedEkModulIds);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, selectedEkModulIds: [...set] };
    });
  };

  const handleSavePaket = async () => {
    if (!form.paketAdi.trim()) {
      toast.warning("Paket adı girin");
      return;
    }
    const payload = { ...form, moduleQrMenu: false, moduleRestoran: false, modulePaketServis: false, moduleUretim: false };
    try {
      setSaving(true);
      if (editId) {
        await axios.put(`${config.baseURL}/gastora-paketler/update`, { paketId: editId, ...payload }, { headers });
        toast.success("Paket güncellendi");
      } else {
        await axios.post(`${config.baseURL}/gastora-paketler/create`, payload, { headers });
        toast.success("Paket oluşturuldu");
      }
      resetDrawer();
      fetchPaketler();
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || "Kayıt başarısız");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateEkModul = async () => {
    if (!newEkModul.kod.trim() || !newEkModul.adi.trim()) {
      toast.warning("Ek modül kodu ve adı zorunlu");
      return;
    }
    try {
      await axios.post(`${config.baseURL}/gastora-ek-moduller/create`, {
        kod: newEkModul.kod,
        adi: newEkModul.adi,
        aciklama: newEkModul.aciklama,
        parentModule: "GASTRO",
      }, { headers });
      toast.success("Ek modül eklendi");
      setNewEkModul({ kod: "", adi: "", aciklama: "", parentModule: "GASTRO" });
      setEkModulDialogOpen(false);
      await fetchEkModuller();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Ek modül eklenemedi");
    }
  };

  const handleDeleteEkModul = async (id) => {
    if (!window.confirm("Bu ek modül tamamen silinsin mi?")) return;
    try {
      await axios.delete(`${config.baseURL}/gastora-ek-moduller/${id}`, { headers });
      toast.success("Ek modül silindi");
      fetchEkModuller();
      setForm((prev) => ({
        ...prev,
        selectedEkModulIds: prev.selectedEkModulIds.filter((x) => x !== id),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.message || "Ek modül silinemedi");
    }
  };

  const handleDeletePaket = async (paketId) => {
    if (!window.confirm("Bu paketi silmek istediğinize emin misiniz?")) return;
    try {
      const res = await axios.delete(`${config.baseURL}/gastora-paketler/${paketId}`, { headers });
      toast.success(res.data?.message || "Paket silindi");
      fetchPaketler();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Paket silinemedi");
    }
  };

  const toggleEtkin = async (id, etkin) => {
    try {
      await axios.patch(`${config.baseURL}/gastora-paketler/${id}/etkin`, { Etkin: etkin }, { headers });
      fetchPaketler();
    } catch {
      toast.error("Paket durumu güncellenemedi");
    }
  };

  if (loading) {
    return (
      <div className="cari-listesi-container">
        <CircularProgress sx={{ m: 4 }} />
      </div>
    );
  }

  return (
    <div className="cari-listesi-container">
    <Box className="packages-container" p={{ xs: 2, sm: 3, md: 4 }}>
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: "#1F2937", mb: 1 }}>
            Gastro Paketler
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280" }}>
            Paket adı Gastro, ana modül Gastro. Ek modülleri siz tanımlarsınız.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddCircleOutline />}
          onClick={openCreateDrawer}
          sx={{ borderRadius: 2, px: 3, py: 1.5, textTransform: "none", backgroundColor: "#1e293b" }}
        >
          Paket Ekle
        </Button>
      </Box>

      <Grid container spacing={3}>
        {[...paketler]
          .sort((a, b) => (Number(a.Sira) || 9999) - (Number(b.Sira) || 9999))
          .map((p, index) => {
            const aktif = !!p.Etkin;
            const paketId = p.PaketId ?? p.Id;
            return (
              <Grid item xs={12} md={6} lg={4} key={paketId}>
                <Fade in timeout={300 + index * 80}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: 2,
                      border: aktif ? "1px solid #E5E7EB" : "1px solid #FECACA",
                      opacity: aktif ? 1 : 0.75,
                    }}
                  >
                    <Box sx={{ backgroundColor: aktif ? "#1e293b" : "#6B7280", p: 2.5, color: "#fff", position: "relative" }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, pr: 12 }}>
                        {p.PaketAdi ?? p.Adi}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.85 }}>
                        Sıra: {p.Sira ?? "-"}
                      </Typography>
                      <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 0.5 }}>
                        <IconButton size="small" sx={{ color: "#fff" }} onClick={() => openEditDrawer(paketId)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" sx={{ color: "#fff" }} onClick={() => handleDeletePaket(paketId)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box sx={{ position: "absolute", top: 8, right: 80 }}>
                        <Switch
                          size="small"
                          checked={aktif}
                          onChange={(e) => toggleEtkin(paketId, e.target.checked)}
                          sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#10B981" } }}
                        />
                      </Box>
                    </Box>
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <People sx={{ color: "#1e293b", fontSize: 20 }} />
                        <Typography variant="body2" color="text.secondary">
                          Max kullanıcı: {p.MaxKullaniciSayisi}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <Apps sx={{ color: "#098f69", fontSize: 18 }} />
                          <Typography variant="subtitle2">Ana modül</Typography>
                        </Box>
                        <Box sx={{ pl: 3.5 }}>
                          <Chip label="Gastro" size="small" color="primary" />
                        </Box>
                      </Box>
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <Extension sx={{ color: "#F59E0B", fontSize: 18 }} />
                          <Typography variant="subtitle2">Ek modüller</Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, pl: 3.5 }}>
                          {(p.EkModuller || []).length ? p.EkModuller.map((em) => (
                            <Chip key={em.Id} label={em.Adi} size="small" variant="outlined" />
                          )) : (
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                              Henüz eklenmedi
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            );
          })}
      </Grid>

      {/* Paket drawer */}
      <Drawer
        open={drawerOpen}
        onClose={resetDrawer}
        direction="right"
        className="add-package-drawer"
        zIndex={600}
        style={{ width: "100%", maxWidth: 920 }}
      >
        <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", backgroundColor: "#F9FAFB" }}>
          <Box sx={{ backgroundColor: "#1e293b", p: 3, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {editId ? "Paket Düzenle" : "Yeni Paket Oluştur"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                variant="contained"
                onClick={handleSavePaket}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
                sx={{ backgroundColor: "#fff", color: "#1e293b", textTransform: "none" }}
              >
                {editId ? "Güncelle" : "Kaydet"}
              </Button>
              <IconButton onClick={resetDrawer} sx={{ color: "#fff" }}>
                <Close />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ p: 3, flex: 1, overflowY: "auto" }}>
            <Card sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Paket Bilgileri</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Paket Adı" required value={form.paketAdi} onChange={(e) => setForm({ ...form, paketAdi: e.target.value })} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth type="number" label="Sıra" value={form.sira} onChange={(e) => setForm({ ...form, sira: e.target.value })} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth type="number" label="Max Kullanıcı" value={form.maxKullaniciSayisi} onChange={(e) => setForm({ ...form, maxKullaniciSayisi: parseInt(e.target.value, 10) || 1 })} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3, mb: 3 }}>
              <Card sx={{ flex: 1, borderRadius: 2 }}>
                <Box sx={{ backgroundColor: "#E8EAF6", p: 2, borderBottom: "2px solid #1e293b", display: "flex", alignItems: "center", gap: 1 }}>
                  <Apps sx={{ color: "#1e293b" }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>Dahili Modüller (Ana)</Typography>
                  <Chip label="1" size="small" sx={{ backgroundColor: "#1e293b", color: "#fff" }} />
                </Box>
                <CardContent>
                  <FormControlLabel
                    control={<Checkbox checked disabled />}
                    label="Gastro"
                  />
                </CardContent>
              </Card>

              <Card sx={{ flex: 1, borderRadius: 2 }}>
                <Box sx={{ backgroundColor: "#FEF3C7", p: 2, borderBottom: "2px solid #F59E0B", display: "flex", alignItems: "center", gap: 1 }}>
                  <Extension sx={{ color: "#F59E0B" }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>Pakete Dahil Ek Modüller</Typography>
                  <Button size="small" variant="outlined" startIcon={<AddCircleOutline />} onClick={openEkModulDialog}>
                    Ek Modül Ekle
                  </Button>
                </Box>
                <CardContent>
                  {availableEkModuller.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Henüz ek modül yok. “Ek Modül Ekle” ile siz ekleyin.
                    </Typography>
                  ) : (
                    availableEkModuller.map((em) => (
                      <Box
                        key={em.Id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          py: 0.75,
                          borderBottom: "1px solid #F3F4F6",
                        }}
                      >
                        <FormControlLabel
                          sx={{ flex: 1, mr: 1 }}
                          control={
                            <Checkbox
                              checked={form.selectedEkModulIds.includes(em.Id)}
                              onChange={() => toggleEkModul(em.Id)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {em.Adi}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {em.Kod}
                                {form.selectedEkModulIds.includes(em.Id) ? " · Pakete dahil" : ""}
                              </Typography>
                            </Box>
                          }
                        />
                        <IconButton size="small" color="error" onClick={() => handleDeleteEkModul(em.Id)} title="Ek modülü sil">
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Ek modül dialog */}
      <Dialog open={ekModulDialogOpen} onClose={() => setEkModulDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Yeni Ek Modül</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Kod ve ad girin. Ana modül Gastro’dur.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Kod" value={newEkModul.kod} onChange={(e) => setNewEkModul({ ...newEkModul, kod: e.target.value.toUpperCase() })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Ad" value={newEkModul.adi} onChange={(e) => setNewEkModul({ ...newEkModul, adi: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Açıklama" value={newEkModul.aciklama} onChange={(e) => setNewEkModul({ ...newEkModul, aciklama: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEkModulDialogOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={handleCreateEkModul}>Ekle</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </div>
  );
}
